export interface RouteMatchOptions {
  currentPath: string;
  currentHash: string | undefined;
  target: string;
  exact?: boolean;
}

export interface BestMatchOptions<T> {
  items: readonly T[];
  currentPath: string;
  currentHash: string | undefined;
  getKey: (item: T) => string;
  getTarget: (item: T) => string;
  getExact?: (item: T) => boolean;
}

function normalizePath(path: string): string {
  const withLeadingSlash = path.startsWith("/") ? path : `/${path}`;
  const trimmed = withLeadingSlash.replace(/\/+$/, "");
  return trimmed.length > 0 ? trimmed : "/";
}

function splitTarget(target: string): { path: string; hash: string | undefined } {
  const [rawPath, rawHash] = target.split("#", 2);
  return {
    path: normalizePath(rawPath ?? "/"),
    hash: rawHash ? `#${rawHash}` : undefined,
  };
}

export function scoreRouteMatch({
  currentPath,
  currentHash,
  target,
  exact = false,
}: RouteMatchOptions): number | null {
  const normalizedCurrentPath = normalizePath(currentPath);
  const { path: targetPath, hash: targetHash } = splitTarget(target);

  const pathMatches = exact
    ? normalizedCurrentPath === targetPath
    : normalizedCurrentPath === targetPath
      || normalizedCurrentPath.startsWith(`${targetPath}/`);

  if (!pathMatches) {
    return null;
  }

  if (targetHash && currentHash !== targetHash) {
    return null;
  }

  const depth = targetPath.split("/").filter(Boolean).length;
  let score = depth * 100 + targetPath.length;

  if (normalizedCurrentPath === targetPath) {
    score += 10_000;
  }

  if (exact) {
    score += 5_000;
  }

  if (targetHash) {
    score += 20_000;
  }

  return score;
}

export function findBestNavigationMatchKey<T>({
  items,
  currentPath,
  currentHash,
  getKey,
  getTarget,
  getExact,
}: BestMatchOptions<T>): string | null {
  let bestKey: string | null = null;
  let bestScore = -1;

  for (const item of items) {
    const score = scoreRouteMatch({
      currentPath,
      currentHash,
      target: getTarget(item),
      exact: getExact?.(item) ?? false,
    });

    if (score === null) {
      continue;
    }

    if (score > bestScore) {
      bestScore = score;
      bestKey = getKey(item);
    }
  }

  return bestKey;
}
